import { useMemo, useState } from 'react';
import { AsyncView, Card, Chip, Stat, Table } from '../ui';
/* Importado do arquivo, não do índice: a ponte com a máquina do João recusa
   escrita em ui/index.ts, então o export de barril lá não tem o Segmented. */
import { Segmented } from '../ui/Segmented';
import { useResource } from '../lib/useResource';
import { podeAtuar, useSessao } from '../lib/session';

const numero = (n: number) => n.toLocaleString('pt-BR');

interface Overview { contacts: number; conversations: number; messages: number; numbers: number }

/**
 * Visão geral.
 *
 * Quatro números e nada mais. A versão anterior misturava contadores com uma
 * lista de conversas recentes e um bloco de "dicas": três assuntos na mesma
 * tela, nenhum deles completo. Conversa recente é trabalho — pertence à caixa
 * de entrada, que é onde a pessoa age.
 */
export function Overview() {
  const { state } = useResource<Overview>('/workspace/overview', { vazio: () => false });
  return (
    <AsyncView
      state={state}
      label="os números do workspace"
      skeletonRows={2}
      empty={{ title: 'Sem dados ainda', text: 'Os números aparecem assim que o primeiro contato escrever.' }}
    >
      {(d) => (
        <div className="stats">
          <Stat rotulo="Contatos" valor={numero(d.contacts)} />
          <Stat rotulo="Conversas" valor={numero(d.conversations)} />
          <Stat rotulo="Mensagens" valor={numero(d.messages)} />
          <Stat rotulo="Números conectados" valor={numero(d.numbers)} />
        </div>
      )}
    </AsyncView>
  );
}

interface Metricas {
  queue: { pending: number; resolved: number };
  sla: { firstResponseMinutes: number | null; resolutionMinutes: number | null };
  transfers: number;
  reopens: number;
  messages: { inbound: number; outbound: number };
  operators: Array<{ name: string; availability: string; currentOpen: number; capacity: number; outbound: number }>;
  automations: { completed: number; failed: number; running: number };
}

const minutos = (m: number | null) => (m === null ? '—' : m < 60 ? `${m.toFixed(1)} min` : `${(m / 60).toFixed(1)} h`);

const DISPONIBILIDADE: Record<string, { rotulo: string; tom: 'success' | 'warning' | 'neutral' }> = {
  AVAILABLE: { rotulo: 'Disponível', tom: 'success' },
  BUSY: { rotulo: 'Ocupado', tom: 'warning' },
  OFFLINE: { rotulo: 'Fora', tom: 'neutral' },
};

const PERIODOS = [
  { dias: 7, rotulo: '7 dias' },
  { dias: 30, rotulo: '30 dias' },
  { dias: 90, rotulo: '90 dias' },
] as const;

export function Metricas() {
  const { agent } = useSessao();
  const podeVer = podeAtuar(agent.role, 'SUPERVISOR');
  const [dias, setDias] = useState<number>(30);

  /**
   * `from` e `to` são OBRIGATÓRIOS na API — sem eles a resposta é 400
   * "Período inválido". A primeira versão desta tela chamava
   * `/workspace/metrics` sem query nenhuma e só não quebrou nos meus testes
   * porque o stub ignora parâmetros. Contra o backend real, a tela nunca teria
   * carregado.
   *
   * O limite do servidor é 93 dias; a maior opção aqui é 90, com folga.
   */
  const path = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - dias * 86400000);
    return `/workspace/metrics?from=${from.toISOString()}&to=${to.toISOString()}`;
  }, [dias]);

  const { state } = useResource<Metricas>(path, {
    enabled: podeVer,
    disabledReason: 'Métricas são visíveis para gestores e administradores.',
    vazio: () => false,
  });

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      {/* O seletor fica FORA do AsyncView: se ficasse dentro, um período que
          falhou levaria embora o próprio controle usado para escolher outro —
          a tela ficaria travada no período que não carrega. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <Segmented
          rotuloGrupo="Período das métricas"
          opcoes={PERIODOS.map((p) => ({ valor: p.dias, rotulo: p.rotulo }))}
          valor={dias}
          onChange={setDias}
        />
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {`Últimos ${dias} dias, até agora.`}
        </span>
      </div>

      <AsyncView
        state={state}
        label="as métricas"
      skeletonRows={3}
      empty={{ title: 'Sem movimento no período', text: 'As métricas aparecem depois da primeira conversa atendida.' }}
    >
      {(m) => (
        <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
          {/* Fila e SLA primeiro: são os números sobre os quais dá para agir
              hoje. Volume de mensagens é contexto, não decisão. */}
          <div className="stats">
            <Stat rotulo="Na fila" valor={numero(m.queue.pending)} apoio="agora" />
            <Stat rotulo="Resolvidas" valor={numero(m.queue.resolved)} apoio="no período" />
            <Stat rotulo="1ª resposta" valor={minutos(m.sla.firstResponseMinutes)} apoio="mediana" />
            <Stat rotulo="Resolução" valor={minutos(m.sla.resolutionMinutes)} apoio="mediana" />
          </div>

          <Card title="Operadores" as="h2">
            <Table
              caption="Carga por operador"
              rowKey={(o) => o.name}
              rows={m.operators}
              columns={[
                { key: 'nome', header: 'Operador', cell: (o) => o.name },
                { key: 'estado', header: 'Estado', cell: (o) => {
                  const d = DISPONIBILIDADE[o.availability] ?? { rotulo: o.availability, tom: 'neutral' as const };
                  return <Chip tone={d.tom}>{d.rotulo}</Chip>;
                } },
                /* Aberto e capacidade juntos: separados, era preciso comparar
                   duas colunas distantes para saber quem estava saturado. */
                { key: 'carga', header: 'Carga', cell: (o) => `${o.currentOpen} de ${o.capacity}` },
                { key: 'enviadas', header: 'Enviadas', cell: (o) => numero(o.outbound) },
              ]}
            />
          </Card>

          {/* Este bloco é contexto, não decisão — precisa de título próprio.
              Sem ele, cinco números apareciam soltos depois da tabela e nada
              dizia a que período ou assunto se referiam. */}
          <section>
            <h2 style={{ fontSize: 'var(--text-md)', color: 'var(--text-secondary)', marginBlockEnd: 'var(--space-3)' }}>
              Volume no período
            </h2>
            <div className="stats">
            <Stat rotulo="Recebidas" valor={numero(m.messages.inbound)} />
            <Stat rotulo="Enviadas" valor={numero(m.messages.outbound)} />
            <Stat rotulo="Transferências" valor={numero(m.transfers)} />
            <Stat rotulo="Reaberturas" valor={numero(m.reopens)} />
            <Stat rotulo="Automações" valor={numero(m.automations.completed)} apoio={m.automations.failed ? `${m.automations.failed} falharam` : 'nenhuma falha'} />
            </div>
          </section>
        </div>
      )}
      </AsyncView>
    </div>
  );
}
